import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import colors from 'common/styles/colors';
import { generateQueryString } from 'common/utils';
import OrgSegmentedBySiteWidget from './OrgSegmentedBySiteWidget';

const WIDGET_NAME = 'org_device_reachability_by_site';

// The row's number is the offline count, so open Devices already narrowed to
// unreachable ones. The Sensors page JSON-parses this param, so the boolean
// survives the query string.
const DEVICES_UNREACHABLE_TARGET = {
  pathname: '/devices',
  search: generateQueryString({ last_measurement: [true] })
};

const SEGMENT_KEYS = ['reachable', 'unreachable', 'unassigned'];

const SEGMENT_COLORS = {
  reachable: colors.normal,
  unreachable: colors.critical,
  unassigned: colors.unknown
};

const SEGMENT_I18N = {
  reachable: 'widgetLibrary.chartLabels.reachable',
  unreachable: 'widgetLibrary.chartLabels.unreachable',
  unassigned: 'widgetLibrary.chartLabels.unassigned'
};

const OrgDeviceReachabilityBySite = (props) => {
  const { t } = useTranslation();

  // Fully-labelled breakdown on hover, for the org card and each site row alike:
  // the headline is only the unreachable slice, so the rest of the bar needs
  // naming somewhere. e.g. "12 Reachable · 1 Unreachable · 3 Unassigned".
  const segmentBreakdown = (entity) => {
    const segments = entity.segments || {};
    const parts = SEGMENT_KEYS
      .filter(key => segments[key])
      .map(key => `${segments[key]} ${t(SEGMENT_I18N[key], key)}`);
    return parts.length
      ? parts.join(' · ')
      : t('orgView.reachability.tooltipNone', 'No devices');
  };

  return (
    <OrgSegmentedBySiteWidget
      {...props}
      widgetName={WIDGET_NAME}
      siteLinkTo={() => DEVICES_UNREACHABLE_TARGET}
      emptyMessageKey="orgView.reachability.noData"
      emptyMessageDefault="No device reachability data"
      summaryLabelKey="orgView.reachability.orgSummary"
      summaryLabelDefault="Organisation device unreachability"
      segmentKeys={SEGMENT_KEYS}
      segmentColors={SEGMENT_COLORS}
      segmentLabelKeys={SEGMENT_I18N}
      // The unreachable slice, over the same denominator as the bar. Reachable %
      // read as a total outage whenever devices were merely unassigned.
      getSummaryValue={summary => `${summary.unreachable_pct != null ? summary.unreachable_pct : 0}%`}
      renderSummaryMeta={(tr, summary) => tr('orgView.reachability.meta', {
        devices: summary.total_devices != null ? summary.total_devices : 0,
        unreachable: summary.unreachable != null ? summary.unreachable : 0,
        defaultValue: '{{devices}} devices · {{unreachable}} offline'
      })}
      // Titled columns + lead with the problem (offline devices), not reachable.
      columnHeaders={{
        bar: t('orgView.reachability.colDevices', 'Unreachability'),
        value: t('orgView.reachability.colOffline', 'Offline')
      }}
      getSiteRowPrimary={(site) => {
        const segments = site.segments || {};
        return segments.unreachable || 0;
      }}
      getValueTone={(site) => {
        const segments = site.segments || {};
        return (segments.unreachable || 0) > 0 ? colors.critical : colors.greyD;
      }}
      getSiteTooltip={segmentBreakdown}
      getSummaryTooltip={segmentBreakdown}
    />
  );
};

OrgDeviceReachabilityBySite.propTypes = {
  organisationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  widgetsMetadata: PropTypes.object,
  title: PropTypes.string
};

OrgDeviceReachabilityBySite.defaultProps = {
  widgetsMetadata: null,
  title: null
};

export default OrgDeviceReachabilityBySite;
